// Which orbit GO's arrival brakes into: LOW, the parking orbit every capture was priced
// into, or HIGH, sized so the body fills a third of the screen and clear of rings and moon paths.

import { AU_KM } from '../world/ephemeris.ts';
import type { BodyRow } from '../world/types.ts';
import { GRAVITATIONAL_CONSTANT_M3_KG_S2 } from './constants.ts';
import { DEFAULT_PARKING_ALTITUDE_KM, sphereOfInfluenceKm } from './transfer.ts';

export type ArrivalOrbit = 'low' | 'high';

/** Vertical field of view of the cruise camera, degrees; `src/cruise/stage.ts` builds its camera off this. */
export const CRUISE_FOV_DEG = 55;
/** Share of the screen's height a body fills from a HIGH arrival. */
export const HIGH_ORBIT_SCREEN_SHARE = 1 / 3;
/** A HIGH orbit stays inside this share of the body's sphere of influence. */
const HIGH_SPHERE_SHARE = 1 / 3;
/** Kept off a moon's path by this many of its Hill radii, or this share of its low point, whichever is more. */
const MOON_MARGIN_HILL_RADII = 5;
const MOON_MARGIN_SHARE = 0.1;
/** Kept outside the outermost ring edge by this share of its radius. */
const RING_MARGIN_SHARE = 0.05;

/** What set a HIGH orbit's radius: the view it is sized for, or the clamp that moved it. */
export type HighOrbitLimit = 'view' | 'sphere' | 'moon' | 'rings' | 'low';

export interface HighOrbit {
  readonly radiusKm: number;
  readonly altitudeKm: number;
  readonly limit: HighOrbitLimit;
  /** The moon whose path set the radius, where one did. */
  readonly moonName: string | null;
  /** The clamps left no room between them, so this is the largest safe radius rather than one clear of every one. */
  readonly squeezed: boolean;
}

/** The share of the screen's height a sphere of `radiusKm` fills from `distanceKm`, at the cruise camera's field of view. */
export function screenShareAt(radiusKm: number, distanceKm: number, fovDeg = CRUISE_FOV_DEG): number {
  if (!(distanceKm > radiusKm) || !(radiusKm > 0)) return Infinity;
  return Math.tan(Math.asin(radiusKm / distanceKm)) / Math.tan((fovDeg * Math.PI) / 360);
}

/** The circular orbit's radius, km, from which a sphere of `radiusKm` fills `share` of the screen's height. */
export function radiusForShareKm(radiusKm: number, share: number, fovDeg = CRUISE_FOV_DEG): number {
  return radiusKm / Math.sin(Math.atan(share * Math.tan((fovDeg * Math.PI) / 360)));
}

/** The push that takes a ship on a circle of `radiusKm` about `body` to escape, km/s. */
export function escapeDvKmS(body: BodyRow, radiusKm: number): number {
  const gm = (GRAVITATIONAL_CONSTANT_M3_KG_S2 * body.mass_kg) / 1e9;
  return gm > 0 && radiusKm > 0 ? (Math.SQRT2 - 1) * Math.sqrt(gm / radiusKm) : 0;
}

export function highOrbitOf(body: BodyRow, rows: readonly BodyRow[]): HighOrbit {
  const radiusKm = body.radius_m / 1000;
  const lowKm = radiusKm + DEFAULT_PARKING_ALTITUDE_KM;
  const done = (r: number, limit: HighOrbitLimit, moonName: string | null, squeezed = false): HighOrbit => ({
    radiusKm: r, altitudeKm: r - radiusKm, limit, moonName, squeezed,
  });
  let r = radiusForShareKm(radiusKm, HIGH_ORBIT_SCREEN_SHARE);
  let limit: HighOrbitLimit = 'view';
  let moonName: string | null = null;

  let ceiling = Infinity;
  let ceilingLimit: HighOrbitLimit = 'view';
  const sphereKm = sphereOfInfluenceKm(body, rows);
  if (sphereKm !== null) {
    ceiling = sphereKm * HIGH_SPHERE_SHARE;
    ceilingLimit = 'sphere';
  }
  // A moon's band is stepped out of, never under, and only to its edge: an orbit inside a path is
  // reached across it, and the higher the orbit, the shallower its approach crosses every path outside.
  const paths: { low: number; high: number; name: string }[] = [];
  for (const moon of rows) {
    if (moon.parent !== body.id || moon.orbit === null || !moon.orbit.parent_relative) continue;
    const aKm = moon.orbit.a_au * AU_KM;
    const lowPoint = aKm * (1 - moon.orbit.e);
    const hill = body.mass_kg > 0 ? aKm * Math.cbrt(moon.mass_kg / (3 * body.mass_kg)) : 0;
    const margin = Math.max(MOON_MARGIN_HILL_RADII * hill, MOON_MARGIN_SHARE * lowPoint);
    const under = lowPoint - margin;
    paths.push({ low: under, high: aKm * (1 + moon.orbit.e) + margin, name: moon.display_name });
  }
  if (r > ceiling) {
    r = ceiling;
    limit = ceilingLimit;
  }
  for (const path of paths.sort((x, y) => x.low - y.low)) {
    if (r > path.low && r < path.high) {
      r = path.high <= ceiling ? path.high : path.low;
      limit = 'moon';
      moonName = path.name;
    }
  }

  const ringsKm = body.rings === undefined ? 0
    : Math.max(0, ...body.rings.components.map((c) => c.outer_m)) / 1000;
  const floor = ringsKm > 0 ? ringsKm * (1 + RING_MARGIN_SHARE) : 0;
  const squeezed = r < floor && floor > ceiling;
  if (r < floor && !squeezed) {
    r = floor;
    limit = 'rings';
    moonName = null;
  }
  return r < lowKm ? done(lowKm, 'low', null, squeezed) : done(r, limit, moonName, squeezed);
}

/** Height over `body`'s radius, km, of the orbit an arrival there brakes into. */
export function arrivalAltitudeKm(
  body: BodyRow, rows: readonly BodyRow[], orbit: ArrivalOrbit, lowAltitudeKm = DEFAULT_PARKING_ALTITUDE_KM,
): number {
  return orbit === 'high' ? Math.max(highOrbitOf(body, rows).altitudeKm, lowAltitudeKm) : lowAltitudeKm;
}
